/**
 * Runtime Configuration
 *
 * Provides environment variables at RUNTIME for the "build once, deploy everywhere" strategy.
 *
 * Problem: Modern.js `source.globalVars` replaces `process.env.X` at BUILD time,
 * baking values into the bundle. This means different environments (tst/stg/prd)
 * would all get the same values.
 *
 * Solution:
 * - Server-side: Read directly from `process.env` (Node.js runtime)
 * - Client-side: Read from `window.__RUNTIME_CONFIG__` (injected by SSR layout loader)
 *
 * Usage in components:
 *   import { useRuntimeConfig } from '@/config/runtimeConfig'
 *   const config = useRuntimeConfig()
 *   console.log(config.API_GATEWAY_URL)
 *
 * Usage in non-React code:
 *   import { getRuntimeConfig } from '@/config/runtimeConfig'
 *   const config = getRuntimeConfig()
 */

import { getApiGatewayUrl } from '@fe-toolkit/axios-api-toolkit'
import { createContext, useContext } from 'react'

import mfeConfigJson from '../../mfe.config.json'

/**
 * Build-time constant for the application base path.
 * Defined in mfe.config.json at the repo root — single source of truth.
 * Use this in components and server code instead of runtimeConfig.BASE_PATH.
 */
export const basePath = mfeConfigJson.basePath

/**
 * Build-time constant for the MFE name.
 * Defined in mfe.config.json at the repo root — single source of truth.
 * Use this in components and server code instead of runtimeConfig.MFE_NAME.
 */
export const mfeName = mfeConfigJson.mfeName

/**
 * Browser-safe runtime config keys.
 * These are serialized into the SSR HTML and exposed to the client via
 * `window.__RUNTIME_CONFIG__`. Only add keys that are safe to expose publicly.
 *
 * These are NOT baked into the build — they come from Kubernetes ConfigMap / env vars.
 */
export const BROWSER_CONFIG_KEYS = [
  'API_GATEWAY_URL',
  'CDN_URL',
  'HOST_URL',
  'GTM_ID',
  'OBSERVABILITY_BASE_URL',
  'ENABLE_MOCKS',
  'NODE_ENV',
  'SNOWPLOW_COLLECTOR_URL',
  'ENABLE_SNOWPLOW',
  // Public mf-manifest URL, resolved at runtime by mf-runtime-remotes-plugin.ts.
  'FE_COMMON_REMOTE',
] as const

/**
 * Server-only config keys.
 * These are available on the server via `process.env` but are NEVER serialized
 * into the SSR HTML or exposed to the browser. Add internal/sensitive URLs here.
 */
export const SERVER_ONLY_CONFIG_KEYS = [
  'SERVER_OBSERVABILITY_BASE_URL',
  // Internal mf-manifest URL for SSR; the public URL is used when it is unset.
  'SERVER_FE_COMMON_REMOTE',
  // In-cluster (service DNS) address of the same API gateway the browser reaches via
  // API_GATEWAY_URL. Used by SSR loaders only, to skip the hairpin out through the
  // WAF/ingress and back. Same gateway, same auth and CSRF semantics. Falls back to
  // API_GATEWAY_URL when unset — see getApiGatewayBaseUrl() below.
  //
  // The address itself is deliberately not written here: this module is part of the
  // client graph, and production builds publish separate .js.map files, so anything in
  // this file's source text is publicly readable. See deployment/k8s/<env>/values.yaml
  // for the value, and docs/api-integration-guide.md for the rationale.
  'SERVER_API_GATEWAY_URL',
] as const

/**
 * All runtime config keys (server + browser).
 */
export const RUNTIME_CONFIG_KEYS = [...BROWSER_CONFIG_KEYS, ...SERVER_ONLY_CONFIG_KEYS] as const

/** Union of browser-safe runtime config key names. */
type BrowserConfigKey = (typeof BROWSER_CONFIG_KEYS)[number]

/** Union of server-only runtime config key names. */
export type ServerOnlyConfigKey = (typeof SERVER_ONLY_CONFIG_KEYS)[number]

/** Union of all runtime config key names (browser + server). */
type RuntimeConfigKey = (typeof RUNTIME_CONFIG_KEYS)[number]

/** Browser-safe runtime config object — all values are strings. */
export type RuntimeConfig = Record<BrowserConfigKey, string>

/** Full runtime config including server-only keys. */
export type FullRuntimeConfig = Record<RuntimeConfigKey, string>

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig
  }
}

/**
 * Build the browser-safe runtime config from the current environment.
 * Called server-side in the layout loader to serialize into the SSR response.
 * Only includes keys from BROWSER_CONFIG_KEYS — server-only values are excluded.
 */
export function buildRuntimeConfig(): RuntimeConfig {
  return BROWSER_CONFIG_KEYS.reduce(
    (acc, key) => {
      acc[key] = process.env[key] || ''

      return acc
    },
    {} as Record<string, string>
  ) as RuntimeConfig
}

/**
 * Safely serialize a runtime config object for embedding in an inline `<script>` tag.
 *
 * Plain `JSON.stringify` is NOT safe inside `<script>` because:
 * - A value containing `</script>` closes the script tag, enabling XSS.
 * - `\u2028` / `\u2029` are valid JSON but are line terminators in JS,
 *   which break string literals in older engines (pre-ES2019).
 *
 * This function replaces every `<`, `\u2028`, and `\u2029` character with its
 * `\uXXXX` Unicode escape in a single pass. On Node 22+ (ES2019+),
 * `JSON.stringify` already escapes `\u2028`/`\u2029`, so only `<` will match
 * in practice — the regex is kept defensive for portability.
 */
export function serializeConfigForScript(config: RuntimeConfig): string {
  return JSON.stringify(config).replace(/[<\u2028\u2029]/g, (char) => {
    return `\\u${char.charCodeAt(0).toString(16).padStart(4, '0')}`
  })
}

/**
 * Get the runtime config from the appropriate source.
 *
 * - Server: Reads from process.env
 * - Client: Reads from window.__RUNTIME_CONFIG__ (set by SSR)
 */
export function getRuntimeConfig(): RuntimeConfig {
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__) {
    return window.__RUNTIME_CONFIG__
  }

  // Server-side: read from process.env (Node.js runtime).
  // In the browser this is a no-op fallback — all values default to '' — because
  // window.__RUNTIME_CONFIG__ is set by an inline <script> in the SSR HTML.
  return buildRuntimeConfig()
}

/**
 * Read a server-only config value.
 *
 * Returns undefined in the browser by construction: SERVER_ONLY_CONFIG_KEYS are never
 * serialized into window.__RUNTIME_CONFIG__, so there is nothing there to read.
 *
 * An empty string is normalised to undefined so that `||` fallbacks behave identically
 * whether a key is absent or blank — Kubernetes values files use "" to mean "not
 * configured" (see the SERVER_FE_COMMON_REMOTE entries in deployment/k8s/local).
 *
 * Note the computed `process.env[key]` access, matching buildRuntimeConfig(). A static
 * `process.env.SERVER_API_GATEWAY_URL` member expression could be substituted at build
 * time by a bundler; computed access cannot, which keeps internal hostnames out of
 * client chunks regardless of how source.globalVars evolves.
 */
export function getServerConfig(key: ServerOnlyConfigKey): string | undefined {
  if (typeof window !== 'undefined') {
    return undefined
  }

  return process.env[key] || undefined
}

/** Fallback gateway URL for local development when nothing is configured. */
export const DEFAULT_API_GATEWAY_URL = 'http://localhost:8081'

/**
 * Resolve the API gateway base URL for the current execution context.
 *
 * - Browser → API_GATEWAY_URL, the public gateway, so traffic passes the WAF/ingress.
 * - Server  → SERVER_API_GATEWAY_URL when set, otherwise API_GATEWAY_URL.
 *
 * Both terminate at the same gateway with the same auth and CSRF semantics; the server
 * variant just uses in-cluster DNS, skipping the hairpin out through the ingress and
 * back. The fallback ordering mirrors createRuntimeRemotesPlugin (see
 * mf-runtime-remotes-plugin.ts), so an environment that does not set the server variant
 * keeps working unchanged — which also makes rollback config-only.
 *
 * The in-cluster address is origin-only. The public URL carries /gateway only because
 * the nginx ingress rewrites it away before the request reaches the gateway service,
 * whose own context-path is /. Repeating that prefix internally 404s every request.
 *
 * Branches on `typeof window`, not on whether window.__RUNTIME_CONFIG__ exists: under
 * jsdom in unit tests `window` is defined but the config is not, and those tests must
 * take the browser branch.
 */
export function getApiGatewayBaseUrl(): string {
  return getApiGatewayUrl({
    isServer: typeof window === 'undefined',
    serverApiGatewayUrl: getServerConfig('SERVER_API_GATEWAY_URL'),
    apiGatewayUrl: getRuntimeConfig().API_GATEWAY_URL || DEFAULT_API_GATEWAY_URL,
  })
}

/**
 * React Context for runtime configuration.
 * Provided by the root layout, available to all components.
 */
const RUNTIME_CONFIG_DEFAULT = {} as RuntimeConfig

export const RuntimeConfigContext = createContext<RuntimeConfig>(RUNTIME_CONFIG_DEFAULT)
/**
 * Hook to access runtime configuration in React components.
 *
 * @example
 * ```tsx
 * const config = useRuntimeConfig()
 * console.log(config.API_GATEWAY_URL)
 * ```
 */
export function useRuntimeConfig(): RuntimeConfig {
  return useContext(RuntimeConfigContext)
}
